import { Injectable } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { Amplify } from 'aws-amplify';
import {
  confirmUserAttribute,
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  sendUserAttributeVerificationCode,
  getCurrentUser,
  resendSignUpCode,
  resetPassword,
  setUpTOTP,
  signIn,
  signOut,
  signUp,
  updateUserAttributes,
  updateMFAPreference,
  updatePassword as updateAuthPassword,
  verifyTOTPSetup,
  type AuthUser,
  type FetchUserAttributesOutput,
  type JWT,
  type SignInOutput,
} from 'aws-amplify/auth';

//import * as AWS from 'aws-sdk';
import { environment } from 'src/environments/environment';
import { ThemingService, ShopTheming } from './theming.service';
import { UserPoolService } from './userPool.service';
import { LambdaService } from './lambda.service';

type LegacyAttributes = Record<string, string>;

interface LegacyToken {
  payload: Record<string, unknown>;
  jwtToken: string;
}

interface LegacySignInSession {
  accessToken: LegacyToken;
  idToken?: LegacyToken;
}

interface LegacyCurrentUser extends AuthUser {
  attributes: LegacyAttributes;
  preferredMFA: string;
  signInUserSession: LegacySignInSession;
}

type SignInStep = SignInOutput['nextStep']['signInStep'];
type ResetPasswordOutput = Awaited<ReturnType<typeof resetPassword>>;
type ResetPasswordStep = ResetPasswordOutput['nextStep']['resetPasswordStep'];
type RecoveryDeliveryMedium = 'EMAIL' | 'SMS' | 'UNKNOWN';

export type MfaChallengeType = 'EMAIL_OTP' | 'SOFTWARE_TOKEN_MFA' | 'SMS_MFA';

export interface PasswordRecoveryDeliveryInfo {
  resetPasswordStep: ResetPasswordStep;
  deliveryMedium: RecoveryDeliveryMedium;
  destinationMasked: string;
  attributeName?: string;
}

type PostPasswordResult =
  | { status: 'MFA_REQUIRED'; challenge: 'EMAIL_OTP' | 'SOFTWARE_TOKEN_MFA' }
  | { status: 'PHONE_VERIFICATION_REQUIRED'; hasPhoneNumber: boolean; destinationMasked?: string }
  | { status: 'SUCCESS' };

type LoginResult =
  | { status: 'MFA_REQUIRED'; user: SignInOutput; challenge: 'EMAIL_OTP' | 'SOFTWARE_TOKEN_MFA' }
  | { status: 'NEW_PASSWORD_REQUIRED'; user: SignInOutput }
  | { status: 'PHONE_VERIFICATION_REQUIRED'; hasPhoneNumber: boolean }
  | { status: 'SUCCESS'; user: SignInOutput };

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly smsMfaLoginNotAllowedError = 'SMS_MFA_LOGIN_NOT_ALLOWED';
  private readonly recoverySmsPhoneNotVerifiedError = 'RECOVERY_SMS_PHONE_NOT_VERIFIED';
  private readonly expiredVerificationSessionError = 'Invalid session for the user, the session is expired';
  private region = '';
  private userPoolId = '';
  private permissions: any = null;
  private users: any[] = [];
  public shopTheme: ShopTheming[] = [];

  private currentUserEmail: string | null = null;
  private currentPassword: string | null = null;
  private pendingPostPasswordResult: SignInOutput | null = null;
  public shopName: string = '';
  // Subjects para notificar cambios
  private userEmailSubject = new BehaviorSubject<string | null>(null);
  private passwordSubject = new BehaviorSubject<string | null>(null);

  // Observables públicos para que los componentes se suscriban
  public userEmail$ = this.userEmailSubject.asObservable();
  public password$ = this.passwordSubject.asObservable();

  constructor(public themingService: ThemingService, public router: Router, private userPoolService: UserPoolService, private lambdaService: LambdaService) {}

  private hasValidShopContext(): boolean {
    const shopGuid = (this.themingService.getShopId() ?? '').trim();
    return shopGuid !== '' && shopGuid !== '0';
  }

  private logAuthPolicyIssue(reason: string, details: Record<string, unknown>): void {
    console.error(`[AuthPolicy] ${reason}`, details);
  }

  private extractErrorMessage(error: unknown): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;
      return typeof message === 'string' ? message : JSON.stringify(error);
    }

    return JSON.stringify(error);
  }

  private extractErrorCode(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return '';
    }

    const candidate = error as { name?: unknown; code?: unknown; __type?: unknown };
    const code =
      (typeof candidate.name === 'string' && candidate.name)
      || (typeof candidate.code === 'string' && candidate.code)
      || (typeof candidate.__type === 'string' && candidate.__type)
      || '';

    if (!code) {
      return '';
    }

    const normalizedCode = code.includes('#') ? code.split('#').pop() ?? code : code;
    return normalizedCode.trim();
  }

  private normalizeDeliveryMedium(medium: string | undefined): RecoveryDeliveryMedium {
    if (medium === 'EMAIL' || medium === 'SMS') {
      return medium;
    }

    return 'UNKNOWN';
  }

  private maskEmail(email: string): string {
    if (email.includes('*')) {
      return email;
    }

    const [namePart, domainPart] = email.split('@');
    if (!namePart || !domainPart) {
      return email;
    }

    if (namePart.length <= 2) {
      return `${namePart[0] ?? '*'}***@${domainPart}`;
    }

    return `${namePart.slice(0, 2)}***@${domainPart}`;
  }

  private maskPhone(phone: string): string {
    if (phone.includes('*')) {
      return phone;
    }

    const digits = phone.replace(/\D/g, '');
    if (digits.length <= 4) {
      return phone;
    }

    return `+${'*'.repeat(Math.max(3, digits.length - 4))}${digits.slice(-4)}`;
  }

  private maskDestination(destination: string | undefined, medium: RecoveryDeliveryMedium): string {
    if (!destination) {
      return '';
    }

    if (medium === 'EMAIL') {
      return this.maskEmail(destination);
    }

    if (medium === 'SMS') {
      return this.maskPhone(destination);
    }

    return destination;
  }

  private normalizeUserAttributes(attributes: FetchUserAttributesOutput): LegacyAttributes {
    const normalized: LegacyAttributes = {};

    Object.entries(attributes).forEach(([key, value]) => {
      if (typeof value === 'string') {
        normalized[key] = value;
      }
    });

    return normalized;
  }

  private tokenToLegacy(token: JWT | undefined): LegacyToken {
    return {
      payload: token?.payload ?? {},
      jwtToken: token?.toString() ?? '',
    };
  }

  private toLegacyPreferredMfa(preferred: string | undefined): string {
    switch (preferred) {
      case 'TOTP':
        return 'SOFTWARE_TOKEN_MFA';
      case 'SMS':
        return 'SMS_MFA';
      case 'EMAIL':
        return 'EMAIL_OTP';
      default:
        return 'NOMFA';
    }
  }

  private getPayloadValue(payload: Record<string, unknown>, key: string): string {
    const value = payload[key];
    return typeof value === 'string' ? value : '';
  }

  private getGroupsFromPayload(payload: Record<string, unknown>): string[] {
    const groups = payload['cognito:groups'];
    if (!Array.isArray(groups)) {
      return [];
    }

    return groups.filter((group): group is string => typeof group === 'string');
  }

  private async buildLegacyCurrentUser(): Promise<LegacyCurrentUser> {
    const [user, attributes, session, mfaPreference] = await Promise.all([
      getCurrentUser(),
      fetchUserAttributes(),
      fetchAuthSession(),
      fetchMFAPreference(),
    ]);

    const signInUserSession: LegacySignInSession = {
      accessToken: this.tokenToLegacy(session.tokens?.accessToken),
      idToken: session.tokens?.idToken
        ? this.tokenToLegacy(session.tokens.idToken)
        : undefined,
    };

    return {
      ...user,
      attributes: this.normalizeUserAttributes(attributes),
      preferredMFA: this.toLegacyPreferredMfa(mfaPreference.preferred),
      signInUserSession,
    };
  }

  private mapStepToLegacyChallenge(signInStep: string): MfaChallengeType | 'NEW_PASSWORD_REQUIRED' | string {
    switch (signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE':
        return 'SOFTWARE_TOKEN_MFA';
      case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE':
        return 'EMAIL_OTP';
      case 'CONFIRM_SIGN_IN_WITH_SMS_CODE':
        return 'SMS_MFA';
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        return 'NEW_PASSWORD_REQUIRED';
      default:
        return signInStep;
    }
  }

  private getPreferredMfaSelection(allowedMfas: readonly string[] | undefined): 'TOTP' | 'EMAIL' | null {
    if (!Array.isArray(allowedMfas) || allowedMfas.length === 0) {
      return null;
    }

    if (allowedMfas.includes('EMAIL')) {
      return 'EMAIL';
    }

    if (allowedMfas.includes('TOTP')) {
      return 'TOTP';
    }

    return null;
  }

  private isMfaSelectionStep(step: string | undefined): boolean {
    return step === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION' || step === 'SELECT_MFA_TYPE';
  }

  private async continueSignInWithPreferredMfaSelection(
    initialResult: SignInOutput
  ): Promise<SignInOutput> {
    let result = initialResult;
    let signInStep = (result.nextStep?.signInStep as string | undefined) ?? '';

    while (this.isMfaSelectionStep(signInStep)) {
      const allowedMfas = (result.nextStep as { allowedMFATypes?: readonly string[] })
        .allowedMFATypes;
      const selectedMfa = this.getPreferredMfaSelection(allowedMfas);

      if (!selectedMfa) {
        this.logAuthPolicyIssue('MFA selection blocked because only SMS is available', {
          signInStep,
          allowedMFATypes: allowedMfas ?? [],
        });
        throw new Error(this.smsMfaLoginNotAllowedError);
      }

      result = await confirmSignIn({
        challengeResponse: selectedMfa,
      });
      signInStep = (result.nextStep?.signInStep as string | undefined) ?? '';
    }

    return result;
  }

  /**
   * The function is called initialize and it configures the Amplify library to use the AWS Cognito
   * User Pool that was created in the AWS Console.
   */

  initialize(): void {
    this.configureAmplify(window.location.pathname);
  }

configureAmplify(pathname: string): void {

  this.userPoolService.getUserPoolId()
    .then(async (data) => {

      this.userPoolId = data.poolId;

      Amplify.configure({
        Auth: {
          Cognito: {
            userPoolId: data.poolId,
            userPoolClientId: data.clientId
          }
        }
      });

      try {
        const session = await fetchAuthSession();

        if (!session?.tokens) {
          // No sesión → ir a login
          this.router.navigate(['/login']);
          return;
        }

        const user = await getCurrentUser();

        if (!user) {
          this.router.navigate(['/login']);
          return;
        }

        const canContinue = await this.enforcePhoneVerificationForActiveSession();
        if (!canContinue) {
          return;
        }

        if (!this.hasValidShopContext()) {
          console.warn('Authenticated session found without valid shop context. Redirecting to login.');
          await this.onSignOut();
          this.router.navigate(['/login']);
          return;
        }

        const themingReady = await this.themingService.initialize();
        if (!themingReady) {
          console.warn('Authenticated session found, but theming context could not be restored. Redirecting to login.');
          await this.onSignOut();
          this.router.navigate(['/login']);
          return;
        }

        const publicAuthRoutes = ['/login', '/recover-password', '/register'];
        const targetPath = publicAuthRoutes.includes(pathname) ? '/home' : pathname;

        // Usuario autenticado → continuar en ruta protegida segura
        this.router.navigate([targetPath]);

      } catch (error) {
        // Cualquier error → login
        this.router.navigate(['/login']);
      }

    })
    .catch((err) => {
      console.error(err);
      this.router.navigate(['/login']);
    });
}

  /**
   * This function takes in a given email and password, and then signs up a user with that email and
   * password.
   * @param {string} givenEmail - The email address of the user.
   * @param {string} givenPassowrd - string - The password that the user entered
   */
  public async onSignUp(givenEmail: string, givenPassowrd: string) {
    await signUp({
      username: givenEmail,
      password: givenPassowrd,
    });
  }

  /**
   * "This function takes in a given email and password, and returns a promise that will resolve to the
   * result of the signIn function from the AWS Amplify Auth module."
   *
   * The above function is a good example of how to use the AWS Amplify Auth module.
   *
   * The AWS Amplify Auth module is a JavaScript library that allows you to integrate AWS Cognito into
   * your JavaScript application.
   *
   * The AWS Amplify Auth module has a signIn function that takes in an object with a username and
   * password property.
   *
   * The signIn function returns a promise that will resolve to the result of the signIn function.
   *
   * The result of the signIn function is an object with a user property.
   *
   * The user property is an object with a username property.
   *
   * The username property is a string that contains the username of the user that was signed in.
   *
   * The username
   * @param {string} givenEmail - The email address of the user.
   * @param {string} givenPassword - string - The password the user entered
   * @returns The return value is a promise.
   */

  // ✅ MFA app (TOTP)

public async setupTotp(user: any): Promise<string> {
  try {
    const setupDetails = await setUpTOTP();
    const issuer = this.themingService.shopTheming[0].shopName || 'Mobydyg';

    const name = typeof user?.attributes?.given_name === 'string' ? user.attributes.given_name.trim() : '';
    const last = typeof user?.attributes?.family_name === 'string' ? user.attributes.family_name.trim() : '';
    const accountName = encodeURIComponent(`${name}.${last}` || user.username);

    const otpAuthUri = `otpauth://totp/${issuer}:${accountName}?secret=${setupDetails.sharedSecret}&issuer=${encodeURIComponent(issuer)}`;
    //console.log('otpUri:', otpAuthUri);
    return otpAuthUri;
  } catch (err) {
    console.error('Error configurando TOTP:', err);
    throw err;
  }
}

public async verifyTotp(code: string): Promise<void> {
  try {

    await verifyTOTPSetup({ code });

    await updateMFAPreference({
      totp: 'PREFERRED',
      email: 'ENABLED'
    });

  } catch (err) {
    console.error('Error verificando TOTP:', err);
    throw err;
  }
}

  public async confirmMfaCode(user: any, code: string, challengeType: MfaChallengeType | string) {
    if (challengeType === 'SMS_MFA') {
      this.logAuthPolicyIssue('SMS MFA challenge blocked during code confirmation', {
        challengeType,
      });
      throw new Error(this.smsMfaLoginNotAllowedError);
    }

    try {
      const result = await confirmSignIn({
        challengeResponse: code,
      });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
        this.logAuthPolicyIssue('Cognito requested SMS code after MFA confirmation', {
          challengeType,
          nextStep: result.nextStep?.signInStep,
        });
        throw new Error(this.smsMfaLoginNotAllowedError);
      }

      if (result.nextStep?.signInStep === 'DONE') {
        await this.syncPreferredMfaAfterSuccessfulAuth();

        const phoneRequirement = await this.getPhoneVerificationRequirement();
        if (phoneRequirement.required) {
          return {
            status: 'PHONE_VERIFICATION_REQUIRED',
            hasPhoneNumber: phoneRequirement.hasPhoneNumber,
          };
        }

        return { status: 'SUCCESS' };
      }

      return { status: 'SUCCESS' };

    } catch (err) {
      console.error('Error al confirmar MFA:', err);
      throw err;
    }
  }

  public async disableTotp(): Promise<void> {
    try {

      await updateMFAPreference({
        totp: 'DISABLED',
        email: 'PREFERRED'
      });

    } catch (err) {
      console.error('Error desactivando TOTP:', err);
      throw err;
    }
  }

  private async syncPreferredMfaAfterSuccessfulAuth(): Promise<void> {
    try {
      const mfaPreference: Awaited<ReturnType<typeof fetchMFAPreference>> =
        await fetchMFAPreference();
      const hasTotp =
        mfaPreference.enabled?.includes('TOTP') === true ||
        mfaPreference.preferred === 'TOTP';

      if (hasTotp) {
        if (mfaPreference.preferred === 'TOTP') {
          return;
        }
        await updateMFAPreference({ totp: 'PREFERRED' });
        return;
      }

      if (mfaPreference.preferred === 'EMAIL') {
        return;
      }

      await updateMFAPreference({ email: 'PREFERRED' });
    } catch (error) {
      console.error('Error aplicando prioridad MFA:', error);
    }
  }

  public async getPhoneVerificationRequirement(): Promise<{
    required: boolean;
    hasPhoneNumber: boolean;
  }> {
    const attributes = await fetchUserAttributes();
    const phoneNumber = attributes['phone_number']?.trim();
    const phoneVerified = attributes['phone_number_verified'] === 'true';

    return {
      required: !phoneNumber || !phoneVerified,
      hasPhoneNumber: !!phoneNumber,
    };
  }

  public async enforcePhoneVerificationForActiveSession(): Promise<boolean> {
    try {
      const requirement = await this.getPhoneVerificationRequirement();

      if (!requirement.required) {
        return true;
      }

      this.logAuthPolicyIssue('Active session blocked because phone is not verified', {
        hasPhoneNumber: requirement.hasPhoneNumber,
      });
      await this.onSignOut();
      await this.router.navigate(['/login']);
      return false;
    } catch (error) {
      console.error('Error enforcing phone verification for active session:', error);
      try {
        await this.onSignOut();
      } catch (signOutError) {
        console.error('Error signing out after phone verification enforcement failure:', signOutError);
      }
      await this.router.navigate(['/login']);
      return false;
    }
  }

public async onLogIn(givenEmail: string, givenPassword: string): Promise<LoginResult> {
  try {

    const signInResult: SignInOutput = await signIn({
      username: givenEmail,
      password: givenPassword,
    });
    const result = await this.continueSignInWithPreferredMfaSelection(signInResult);
    const signInStep = result.nextStep.signInStep as SignInStep | string;

    this.currentUserEmail = givenEmail;
    this.currentPassword = givenPassword;
    this.userEmailSubject.next(givenEmail);
    this.passwordSubject.next(givenPassword);

    if (signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
      this.logAuthPolicyIssue('SMS challenge received during login', {
        signInStep,
        username: givenEmail,
      });
      throw new Error(this.smsMfaLoginNotAllowedError);
    }

    if (
      signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
      signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
    ) {
      const challenge: 'EMAIL_OTP' | 'SOFTWARE_TOKEN_MFA' =
        signInStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
          ? 'EMAIL_OTP'
          : 'SOFTWARE_TOKEN_MFA';

      return {
        status: 'MFA_REQUIRED',
        user: result,
        challenge,
      };
    }

    if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return {
        status: 'NEW_PASSWORD_REQUIRED',
        user: result
      };
    }

    if (signInStep === 'DONE') {
      await this.syncPreferredMfaAfterSuccessfulAuth();
      const phoneRequirement = await this.getPhoneVerificationRequirement();

      if (phoneRequirement.required) {
        return {
          status: 'PHONE_VERIFICATION_REQUIRED',
          hasPhoneNumber: phoneRequirement.hasPhoneNumber,
        };
      }
    }

    return {
      status: 'SUCCESS',
      user: result
    };

  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
}

  public async resendSignInVerificationCode(): Promise<LoginResult> {
    const storedEmail = this.currentUserEmail?.trim();
    const storedPassword = this.currentPassword;

    if (!storedEmail || !storedPassword) {
      throw new Error(this.expiredVerificationSessionError);
    }

    try {
      await signOut();
    } catch (error) {
      console.warn('Unable to clear pending sign-in before resending verification code.', error);
    }

    return this.onLogIn(storedEmail, storedPassword);
  }

  public getCurrentUserEmail(): string | null {
    return this.currentUserEmail;
  }

  public getCurrentPassword(): string | null {
    return this.currentPassword;
  }

  public async getCurrentUserDetails() {
    const user = await this.currentAuthUser();
    const payload = user.signInUserSession.idToken?.payload ?? {};
    const roleFromPayload = this.getPayloadValue(payload, 'custom:user_role');

    return {
      email: this.currentUserEmail,
      username: user.username,
      password: this.currentPassword,
      attributes: user.attributes,
      given_name: user.attributes?.['given_name'],
      family_name: user.attributes?.['family_name'],
      sub: this.getPayloadValue(payload, 'sub'),
      role: roleFromPayload || user.attributes?.['custom:user_role'],
    };
  }

  /**
   * It returns the current authenticated user, bypassing the cache
   * @returns The current authenticated user.
   */
  public async currentAuthUser() {
    return this.buildLegacyCurrentUser();
  }

  public async getUserRole(): Promise<string> {
    try {
      const user = await this.currentAuthUser();
      const userRole = user.attributes['custom:user_role'];
      return userRole;
    } catch (error) {
      console.error('Error al obtener el rol del usuario:', error);
      throw error;
    }
  }

  public async getUserPermissions() {
    if (this.permissions) {
      return this.permissions;
    }
    try {
      const user = await this.currentAuthUser();
      const groups = this.getGroupsFromPayload(user.signInUserSession.accessToken.payload);
      const isOwner = groups.includes('Owner') ||
                      user.attributes['custom:user_role'] === 'Owner' || user.attributes['custom:user_role'] === 'Admin';
      let permissions = {
        home: false,
        sales: false,
        cashRegisters: false,
        products: false,
        giftCards: false,
        coupons: false,
        clients: false,
        calendar: false,
        config: false,
        generalInfo: false,
        userRoles: false,
        categories: false,
        collections: false,
        supliers: false,
        shopPoints: false,
        paymentMethods: false,
        themingService: false,
        faqs: false,
        exportClients: false,
        pos: false,
        pages: false,
        analytics: false,
        policies: false,
        emailTemplate: false,
        shippingCosts: false,
        chatApp: false,
      };

      if (isOwner) {
        permissions = {
          home: true,
          sales: true,
          cashRegisters: true,
          products: true,
          giftCards: true,
          coupons: true,
          clients: true,
          calendar: true,
          config: true,
          generalInfo: true,
          userRoles: true,
          categories: true,
          collections: true,
          supliers: true,
          shopPoints: true,
          paymentMethods: true,
          themingService: true,
          faqs: true,
          exportClients: true,
          pos: true,
          pages: true,
          analytics: true,
          policies: true,
          emailTemplate: true,
          shippingCosts: true,
          chatApp: true,
        };
      } else {
        const permissionsAttr = user.attributes['custom:permissions'];
        if (permissionsAttr) {
          permissions = JSON.parse(permissionsAttr);
        }
      }
      this.permissions = permissions;
      return permissions;
    } catch (error) {
      console.error('Error obteniendo los permisos del usuario', error);
      throw error;
    }
  }

  private async resolvePendingPostPasswordSignIn(): Promise<
    { status: 'MFA_REQUIRED'; challenge: 'EMAIL_OTP' | 'SOFTWARE_TOKEN_MFA' }
    | { status: 'SUCCESS' }
  > {
    const pendingResult = this.pendingPostPasswordResult;
    this.pendingPostPasswordResult = null;

    if (!pendingResult) {
      throw new Error('No pending sign-in challenge after password update.');
    }

    const result = await this.continueSignInWithPreferredMfaSelection(pendingResult);
    const nextStep = result.nextStep?.signInStep as SignInStep | string;

    if (nextStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
      this.logAuthPolicyIssue('SMS challenge received after NEW_PASSWORD_REQUIRED', {
        nextStep,
      });
      throw new Error(this.smsMfaLoginNotAllowedError);
    }

    if (nextStep === 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE') {
      return {
        status: 'MFA_REQUIRED',
        challenge: 'EMAIL_OTP'
      };
    }

    if (nextStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      return {
        status: 'MFA_REQUIRED',
        challenge: 'SOFTWARE_TOKEN_MFA'
      };
    }

    if (nextStep === 'DONE') {
      await this.syncPreferredMfaAfterSuccessfulAuth();
      return { status: 'SUCCESS' };
    }

    throw new Error(
      `Unsupported sign-in step after onboarding: ${nextStep ?? 'UNKNOWN'}`
    );
  }

  /**
   * This function is called when the user has signed up with a new password and the user has been
   * authenticated
   * @param {any} user - The user object that was returned by the Auth.signIn() method.
   * @param {string} newPassword - The new password that the user has entered.
   */
  public async onNewPasswordRequired(newPassword: string): Promise<PostPasswordResult> {
    try {
      const confirmResult = await confirmSignIn({
        challengeResponse: newPassword
      });

      this.pendingPostPasswordResult = confirmResult;
      const phoneRequirement = await this.getPhoneVerificationRequirement();

      if (phoneRequirement.required) {
        if (phoneRequirement.hasPhoneNumber) {
          const verification = await this.startPhoneNumberVerification();
          return {
            status: 'PHONE_VERIFICATION_REQUIRED',
            hasPhoneNumber: true,
            destinationMasked: verification.destinationMasked,
          };
        }

        return {
          status: 'PHONE_VERIFICATION_REQUIRED',
          hasPhoneNumber: false,
        };
      }

      return this.resolvePendingPostPasswordSignIn();

    } catch (error) {
      this.pendingPostPasswordResult = null;
      console.error('Error completando nueva contraseña:', error);
      throw error;
    }
  }

  public async startPhoneNumberVerification(
    phoneNumber?: string
  ): Promise<{ destinationMasked: string }> {
    if (phoneNumber && phoneNumber.trim()) {
      await updateUserAttributes({
        userAttributes: {
          phone_number: phoneNumber.trim(),
        },
      });
    }

    const verificationResult = await sendUserAttributeVerificationCode({
      userAttributeKey: 'phone_number',
    });
    const deliveryDetails =
      (verificationResult as { deliveryDetails?: { destination?: string; deliveryMedium?: string } })
        .deliveryDetails
      ?? (verificationResult as { codeDeliveryDetails?: { destination?: string; deliveryMedium?: string } })
        .codeDeliveryDetails;
    const deliveryMedium = this.normalizeDeliveryMedium(deliveryDetails?.deliveryMedium);
    const destinationMasked = this.maskDestination(deliveryDetails?.destination, deliveryMedium);

    return { destinationMasked };
  }

  public async confirmPhoneNumberVerification(code: string): Promise<
    | { status: 'MFA_REQUIRED'; challenge: 'EMAIL_OTP' | 'SOFTWARE_TOKEN_MFA' }
    | { status: 'SUCCESS' }
  > {
    await confirmUserAttribute({
      userAttributeKey: 'phone_number',
      confirmationCode: code,
    });

    if (!this.pendingPostPasswordResult) {
      await this.syncPreferredMfaAfterSuccessfulAuth();
      return { status: 'SUCCESS' };
    }

    return this.resolvePendingPostPasswordSignIn();
  }

  /**
   * This function will resend the confirmation code to the user's email address.
   * @param {string} givenEmail - The email address of the user who needs a new confirmation code.
   */
  public async resendConfirmationCode(givenEmail: string) {
    await resendSignUpCode({ username: givenEmail });
  }

  /**
   * This function takes a given email, and sends a password reset email to that email address.
   * @param {string} givenEmail - The email address of the user who has forgotten their password.
   */
  public async forgotPassword(givenEmail: string): Promise<PasswordRecoveryDeliveryInfo> {
    console.log("FORGOT PASSWORD CALLED");
    const result: ResetPasswordOutput = await resetPassword({ username: givenEmail });
    const nextStep = result.nextStep;
    const deliveryDetails = nextStep.codeDeliveryDetails;
    const deliveryMedium = this.normalizeDeliveryMedium(deliveryDetails?.deliveryMedium);
    const destinationMasked = this.maskDestination(deliveryDetails?.destination, deliveryMedium);

    return {
      resetPasswordStep: nextStep.resetPasswordStep as ResetPasswordStep,
      deliveryMedium,
      destinationMasked,
      attributeName: deliveryDetails?.attributeName,
    };
  }

  /**
   * "This function takes in a given email, a given code, and a given password, and then it submits the
   * given email, the given code, and the given password to the AWS Cognito forgotPasswordSubmit
   * function."
   * </code>
   * @param {string} givenEmail - The email address of the user who is changing their password.
   * @param {string} givenCode - The code sent to the user via email or other delivery mechanism.
   * @param {string} givenPassword - The new password.
   */
  public async changePassword(
    givenEmail: string,
    givenCode: string,
    givenPassword: string
  ) {
    try {
      await confirmResetPassword({
        username: givenEmail,
        confirmationCode: givenCode,
        newPassword: givenPassword,
      });
    } catch (error) {
      const errorMessage = this.extractErrorMessage(error);
      const normalized = errorMessage.toLowerCase();
      if (normalized.includes('phone_number') && normalized.includes('verified')) {
        console.error('Password reset blocked: phone_number is not verified.', {
          username: givenEmail,
          errorMessage,
        });
        throw new Error(this.recoverySmsPhoneNotVerifiedError);
      }

      throw error;
    }
  }

  /**
   * This function will sign the user out of the application and redirect them to the sign in page.
   */
  public async onSignOut() {
    try {
      await signOut({ global: true });
    } catch (error) {
      console.warn('Sign out failed while cleaning auth state.', error);
    } finally {
      this.currentUserEmail = null;
      this.currentPassword = null;
      this.pendingPostPasswordResult = null;
      this.userEmailSubject.next(null);
      this.passwordSubject.next(null);

      // borrar el cookie de shopGuid
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate());
      document.cookie = `shopGuid=0; expires=${expirationDate.toUTCString()};`;
    }
  }

  /**
   * It returns a promise that resolves to an array of users.
   * @returns The users array is being returned.
   */
  public async getUsers() {

    await this.lambdaService.cognitoInstruction('listUsers', {
      userPoolId: this.userPoolId
    }).then((users: any) => {
      this.users = users;
    });
    return this.users;

  }

  /**
   * It takes a username as a parameter, and returns a promise that resolves to an object containing the
   * user's data.
   * @param {string} username - The username of the user you want to get information about.
   * @returns The user data is being returned.
   */
  public async getUser(username: string) {
    let userdata;
    await this.lambdaService.cognitoInstruction('getUser', {
      userPoolId: this.userPoolId,
      username
    }).then((user: any) => {
      userdata = user;
    });

    return userdata;

  }

  public async listGroups() {
    let groupsData;
    await this.lambdaService.cognitoInstruction('listGroups', {
      userPoolId: this.userPoolId
    }).then((groups: any) => {
      groupsData = groups
    });

    return groupsData;

  }

  public async adminAddUserToGroup(username: string, groupName: string) {

    await this.lambdaService.cognitoInstruction('adminAddUserToGroup', {
      userPoolId: this.userPoolId,
      username: username,
      groupName: groupName
    }).then((response: any) => {
      console.log('Resultado:', response.message); // "Usuario asignado al grupo correctamente"
    });
  }

  public async createUserAdmin(
    given_name: string,
    family_name: string,
    email: string,

    user_role: string,
    temporary_password: string,
    permissions: any,
    shop_access: string
  ) {

    const payload: Record<string, unknown> = {
      userPoolId: this.userPoolId,
      email: email,
      email_verified: 'true',
      given_name: given_name,
      family_name: family_name,
      user_role: user_role,
      temporary_password: temporary_password, // debe cumplir política de Cognito
      shop_access: shop_access,
      permissions: permissions
    };

    let resp;
    await this.lambdaService.cognitoInstruction('adminCreateUser', payload).then((response: any) => {
      console.log('Usuario creado:', response);
      resp = response;
    });

    return resp;

  }

  public async deleteUser(username: string) {

    await this.lambdaService.cognitoInstruction('adminDeleteUser', {
      userPoolId: this.userPoolId,
      username: username
    }).then((response: any) => {
      console.log('Resultado:', response.message); // "Usuario eliminado exitosamente"
    });
  }

  public async disableUserAccess(username: string) {

    await this.lambdaService.cognitoInstruction('adminDisableUser', {
      userPoolId: this.userPoolId,
      username: username
    }).then((response: any) => {
      console.log('Resultado:', response.message); // "Usuario deshabilitado exitosamente"
    });


  }

  public async enableUserAccess(username: string) {

    this.lambdaService.cognitoInstruction('adminEnableUser', {
      userPoolId: this.userPoolId,
      username: username
    }).then((response: any) => {
      console.log('Resultado:', response.message); // "Usuario deshabilitado exitosamente"
    });

  }

  public async updateUser(
    username: string,
    given_name: string,
    family_name: string,
    phone_number: string | null,
    permissions: any,
    shop_access: string
  ) {
    const normalizedPhone = phone_number && phone_number.trim() !== ''
      ? phone_number
      : null;

    return this.lambdaService.cognitoInstruction('adminUpdateUserAttributes', {
      userPoolId: this.userPoolId,
      username,
      given_name,
      family_name,
      phone_number: normalizedPhone,
      shop_access,
      permissions
    }).then((response: any) => {
      console.log('Resultado:', response.message); // "Usuario actualizado exitosamente"
      return response;
    });
  }


  public async updatePassword(oldPassword: string, newPassword: string) {
      return updateAuthPassword({
        oldPassword,
        newPassword,
      });
    // let params = {
    //   AccessToken: (await Auth.currentSession()).getAccessToken().getJwtToken(),
    //   PreviousPassword: oldPassword,
    //   ProposedPassword: newPassword,
    // };
    // return new Promise((resolve, reject) => {
    //   let cognitoidentityserviceprovider =
    //     new AWS.CognitoIdentityServiceProvider();
    //   cognitoidentityserviceprovider.changePassword(params, (err, data) => {
    //     if (err) {
    //       reject(err);
    //     } else {
    //       resolve(data);
    //     }
    //   });
    // });
  }

  /**
   * It handles errors.
   * @param {String} message - String, givenEmail: string
   * @param {string} givenEmail - string
   */
  public translateAuthError(error: unknown, givenEmail: string): string {
    const errorCode = this.extractErrorCode(error);
    const normalizedMessage = this.extractErrorMessage(error);
    const lowerCaseMessage = normalizedMessage.toLowerCase();

    if (
      normalizedMessage === this.expiredVerificationSessionError
      || lowerCaseMessage.includes('invalid session for the user')
      || lowerCaseMessage.includes('session is expired')
    ) {
      return 'Tu sesión de verificación expiró. Por favor inicia sesión nuevamente.';
    }

    switch (errorCode) {
      case 'CodeMismatchException':
        return 'El código ingresado es incorrecto.';

      case 'ExpiredCodeException':
        return 'El código ha expirado. Solicita uno nuevo.';

      case 'TooManyRequestsException':
        return 'Has solicitado demasiados códigos. Espera un momento antes de intentar nuevamente.';

      case 'UserNotFoundException':
        return 'El usuario no existe.';

      case 'NotAuthorizedException':
        return 'Correo o contraseña incorrectos.';
    }

    switch (normalizedMessage) {
      case 'SMS_MFA_LOGIN_NOT_ALLOWED':
        return 'Tu cuenta no puede iniciar sesión con SMS. Usa Email OTP o la app autenticadora (TOTP).';

      case 'RECOVERY_SMS_PHONE_NOT_VERIFIED':
        return 'No se pudo recuperar la contraseña por SMS porque tu teléfono no está verificado.';

      case 'User does not exist.':
        return 'El usuario no existe.';

      case 'Incorrect username or password.':
        return 'Correo o contraseña incorrectos.';

      case 'User is not confirmed.':
        this.resendConfirmationCode(givenEmail).catch((error) => {
          console.error('No se pudo reenviar el correo de confirmación:', error);
        });
        return 'Por favor, verifique su cuenta con el enlace enviado a su correo';

      case 'An account with the given email already exists.':
        return 'Este correo ya está registrado, inicia sesión para continuar';

      case 'Pending sign-in attempt already in progress':
        return 'Ya hay un intento de inicio de sesión pendiente en progreso';

      case 'Username/client id combination not found.':
        return 'Este correo no posee una cuenta registrada';

      case 'Attempt limit exceeded, please try after some time.':
        return 'Has solicitado demasiados códigos. Espera un momento antes de intentar nuevamente.';

      case 'Cannot reset password for the user as there is no registered/verified email or phone_number':
        this.resendConfirmationCode(givenEmail).catch((error) => {
          console.error('No se pudo reenviar el correo de verificación:', error);
        });
        return 'No es posible recuperar la contraseña porque el usuario no tiene email ni teléfono verificado.';

      case 'Invalid verification code provided, please try again.':
        return 'El código ingresado es incorrecto.';

      case 'Invalid code provided, please request a code again.':
        return 'El código ha expirado. Solicita uno nuevo.';

      case 'Password attempts exceeded':
        return 'Intentos de contraseña excedidos, cambie su contraseña';

      case 'PreAuthentication failed with error Cannot authenticate users without admin role.':
        return 'Usuario no autorizado';

      case 'Temporary password has expired and must be reset by an administrator.':
        return 'La contraseña temporal ha expirado y debe ser reseteada por un administrador';

      case 'User password cannot be reset in the current state.':
        return 'La contraseña no puede ser cambiada en este estado';

      case 'An account with the given email already exists.backoffice':
        return 'Este usuario ya está registrado en tu tienda';

      case 'User is disabled.':
        return 'El usuario está deshabilitado';

      default:
        if (lowerCaseMessage.includes('phone_number') && lowerCaseMessage.includes('not verified')) {
          return 'Tu teléfono no está verificado para recuperación por SMS.';
        }

        return 'Ocurrió un error durante la autenticación.';
    }
  }

  handleErrors(message: unknown, givenEmail: string) {
    return this.translateAuthError(message, givenEmail);
  }

  /**
   * If the password and confirm password fields are not equal, set the confirm password field to have
   * an error of matching: true.
   * @param {string} password - string - The name of the password field
   * @param {string} ConfirmPassword - The name of the control that you want to validate.
   * @returns a validator function.
   */
  public confirmPasswordValidator(
    password: string,
    ConfirmPassword: string
  ): ValidatorFn {
    return (controls: AbstractControl) => {
      const control = controls.get(password);
      const checkControl = controls.get(ConfirmPassword);
      if (checkControl?.errors && !checkControl.errors['matching']) {
        return null;
      }
      if (control?.value !== checkControl?.value) {
        controls.get(ConfirmPassword)?.setErrors({ matching: true });
        return { matching: true };
      } else {
        return null;
      }
    };
  }
}

/* An animation. */
export const alertsAnimation = trigger('inOutAnimation', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('100ms', style({ opacity: 1 })),
  ]),
  transition(':leave', [animate('0ms', style({ opacity: 0 }))]),
]);

/* If the user is authenticated, return true, otherwise navigate to the home page and return false. */
@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate {
  constructor(private router: Router, private authService: AuthService) {}
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.authService.currentAuthUser()
      .then(async () => {
        return this.authService.enforcePhoneVerificationForActiveSession();
      })
      .catch(() => {
        return false;
      });
  }
}
/* If the user is authenticated and is a member of the Owner group, then the user can access the route.
Otherwise, the user is redirected to the login page. */
@Injectable({
  providedIn: 'root',
})
export class AuthGuardOwner implements CanActivate {
  constructor(private router: Router, private authService: AuthService) {}
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.authService.currentAuthUser()
      .then(async (user: LegacyCurrentUser) => {
        const canContinue = await this.authService.enforcePhoneVerificationForActiveSession();
        if (!canContinue) {
          return false;
        }

        const groups = Array.isArray(user.signInUserSession.accessToken.payload['cognito:groups'])
          ? user.signInUserSession.accessToken.payload['cognito:groups']
          : [];
        if (
          groups.includes(
            'Owner'
          ) ||
          user.attributes['custom:user_role'] == 'Owner'
        ) {
          return true;
        } else {
          return false;
        }
      })
      .catch(() => {
        return false;
      });
  }
}

/* If the user is authenticated, redirect to the my-shops route, otherwise allow the user to continue
to the route they are trying to access.
</code> */
@Injectable({
  providedIn: 'root',
})
export class UnauthGuard implements CanActivate {
  constructor(private router: Router) {}
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return getCurrentUser()
      .then(() => {
        this.router.navigate(['/home']);
        return false;
      })
      .catch(() => {
        return true;
      });
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuardPermission implements CanActivate {
  constructor(private authService: AuthService, private router: Router) {}

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.authService.currentAuthUser()
      .then(async () => {
        const canContinue = await this.authService.enforcePhoneVerificationForActiveSession();
        if (!canContinue) {
          return false;
        }

        return this.authService.getUserPermissions()
          .then(async (permissions) => {
            const requiredPermission = next.data['permission'];
            const userRole = await this.authService.getUserRole();

            if (userRole === 'Owner' || userRole === 'Admin') {
              return true;
            }

            if (
              typeof requiredPermission === 'string' &&
              typeof permissions === 'object' &&
              permissions !== null &&
              permissions[requiredPermission] === true
            ) {
              return true;
            } else {
              this.router.navigate(['home']);
              return false;
            }
          });
      })
      .catch((error) => {
        console.error('Error capturado:', error);
        if (error === 'not authenticated' || error?.message?.includes('User is not authenticated')) {
          this.router.navigate(['/home']);
        } else {
          console.error('Otro tipo de error:', error);
        }

        return false;
      });

  }
}
